import { getFragmentData, ResolvableModeEnum } from '@e-socna/data-layer';
import { invariant } from '@e-socna/utils';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { FormVariant } from '../enums/variant.enum';

import { ResolvableServiceFormTab } from './enums/tab.enum';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { useResolvableServiceSubmit } from './hooks/useSubmit.hook';
import { useResolvableServiceSteps } from './hooks/useSteps.hook';
import { useResolvableServiceSchema } from './hooks/useResolvableServiceSchema.hook';
import { ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment } from './fragments/resolvable-service-base-interface.fragment';
import type { ResolvableServiceMutationResult } from './hooks/useSubmit.hook';
import { InfoAlert } from '../../components/InfoAlert';

export { ResolvableServiceFormTab };

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<
    typeof ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment
  >;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: ResolvableServiceMutationResult) => void;
  readonly resolvableServiceId?: Id;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly ResolvableServiceFormTab[];
  readonly title?: string;
}

export const ResolvableServiceForm = ({
  askMemoBeforeSubmit = true,
  formVariant,
  initialValues: ppInitialValues,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  resolvableServiceId,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || resolvableServiceId),
    'You must provide a resolvableServiceId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    ResolvableServiceForm_ResolvableServiceBaseInterface_Fragment,
    ppInitialValues,
  );

  const { initialValues } = useInitialValuesFactory(pInitialValues);

  const { handleSubmit } = useResolvableServiceSubmit({
    formVariant,
    handleSuccess,
    resolvableServiceId,
  });

  const {
    fileDirectoryCategoriesCodeMap,
    resolvableServiceFileCategoriesCodeMap,
  } = useReferencesContext();

  const { steps, wizardChildren } = useResolvableServiceSteps({
    documentResolversSettings: {
      allowedReturnTypeIds: undefined,
      enableDirectGeneration: false,
      mustBeResolvableOnBack: false,
    },
    resolvableFieldSettings: {
      enableDirectGeneration: false,
      primaryMode: ResolvableModeEnum.Static,
    },
    stepIdsSubset,
    storage: {
      directoryCategoryId: fileDirectoryCategoriesCodeMap.ResolvableService._id,
      entityId: resolvableServiceId ?? initialValues._id,
      imageCategory: resolvableServiceFileCategoriesCodeMap.TextEditorImages,
    },
  });

  const ResolvableServiceSchema = useResolvableServiceSchema();

  if (pInitialValues && !pInitialValues.value) {
    return (
      <InfoAlert>
        Les montants de cette prestation sont confidentiels : vous ne pouvez pas
        la modifier.
      </InfoAlert>
    );
  }

  return (
    <Wizard
      ValidationSchema={ResolvableServiceSchema}
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
